import { useLocale } from '../hooks/useLocale.jsx';

// Standalone public-web page satisfying Google Play's "delete account URL"
// requirement: reachable WITHOUT installing the app, references the app /
// developer name, lists deletion steps, specifies what is deleted vs kept
// along with retention windows.
// https://support.google.com/googleplay/android-developer/answer/13327826
//
// Localized into the same three languages the app itself supports so that
// KR / JP users landing on the page see content matching the menu names
// they actually see in the app.
export function DeleteAccount() {
  const { lang } = useLocale();
  if (lang === 'ko') return <DeleteAccountKo />;
  if (lang === 'ja') return <DeleteAccountJa />;
  return <DeleteAccountEn />;
}

// 정적 HTML (scripts/prerender-legal.mjs) 용 — 크롤러·앱 없는 방문자에게 세 언어를
// 한 장에. Play 는 이 URL 을 "계정 삭제 URL" 로 심사하는데, SPA 껍데기만 나가면
// /privacy 처럼 "유효하지 않은 페이지" 로 걸린다.
export function DeleteAccountStatic() {
  return (
    <>
      <nav className="legal-content legal-lang-nav" aria-label="Language">
        <a href="#en">English</a> · <a href="#ko">한국어</a> · <a href="#ja">日本語</a>
      </nav>
      <div id="en" lang="en"><DeleteAccountEn /></div>
      <div id="ko" lang="ko"><DeleteAccountKo /></div>
      <div id="ja" lang="ja"><DeleteAccountJa /></div>
    </>
  );
}

// ─── English ─────────────────────────────────────────────────────────────
function DeleteAccountEn() {
  return (
    <div className="legal-page">
      <main className="legal-content">
        <h1>Delete your archelier account</h1>
        <p className="legal-updated">Last updated: October 2026</p>

        <p>
          You can permanently delete your <strong>archelier</strong> account
          and all associated data at any time. Two ways are available:
        </p>

        <h2>Option 1 — In the app (recommended, immediate)</h2>
        <ol>
          <li>Open archelier on iOS or Android, or visit{' '}
            <a href="https://archelier.co" target="_blank" rel="noreferrer">archelier.co</a>.</li>
          <li>Sign in with the Google or Apple account you used to register.</li>
          <li>Tap the <strong>Settings</strong> tab (bottom-right on mobile,
            or the avatar menu on the desktop top bar).</li>
          <li>Scroll to the bottom of the Settings page → tap <strong>Delete Account</strong>.</li>
          <li>Confirm. Your account and all data are removed within minutes.</li>
        </ol>

        <h2>Delete only specific data (without deleting your account)</h2>
        <p>
          You can also delete individual items in the app without removing your
          whole account:
        </p>
        <ul>
          <li><strong>Individual designs</strong> — open the design from My Designs and tap the trash icon.</li>
          <li><strong>Comments</strong> — long-press your comment and tap delete.</li>
          <li><strong>Bookmarks / collections</strong> — open the collection and remove items individually.</li>
          <li><strong>Profile photo / display name / bio</strong> — Settings → Edit profile.</li>
          <li><strong>Follows or blocks</strong> — unfollow from any profile, manage blocks at Settings → Blocked users.</li>
          <li><strong>Gio conversations</strong> — individual messages can&apos;t be removed in the app yet; email us (below) and we delete them.</li>
        </ul>
        <p>
          If you need a specific item deleted that isn't covered by the in-app
          controls, email{' '}
          <a href="mailto:hello@uhzlab.com?subject=Delete%20specific%20archelier%20data">
            hello@uhzlab.com
          </a>{' '}
          describing the item — we delete it within 7 days.
        </p>

        <h2>Option 2 — By email (no app needed)</h2>
        <p>
          If you no longer have the app installed, email{' '}
          <a href="mailto:hello@uhzlab.com?subject=Delete%20my%20archelier%20account">
            hello@uhzlab.com
          </a>{' '}
          with the subject <em>"Delete my archelier account"</em> from the email
          address you signed in with. We confirm and complete the deletion within
          7 days.
        </p>

        <h2>What is deleted</h2>
        <ul>
          <li>Your account profile — display name, handle, bio, profile photo</li>
          <li>All designs you created, including unposted drafts and edits</li>
          <li>Every photo you uploaded to the service</li>
          <li>Your conversations with Gio, the AI design assistant, and the photos you attached to them</li>
          <li>Push notification tokens for your devices</li>
          <li>All comments, likes, follows, bookmarks, and collections</li>
          <li>All reports you submitted and blocks you set</li>
          <li>Your subscription state and credit balance, including purchased credits (remaining credits are not refunded)</li>
        </ul>

        <h2>What is kept</h2>
        <ul>
          <li>
            <strong>Anonymized analytics data</strong> — aggregated, non-personally-
            identifiable usage events used to improve service quality.
          </li>
          <li>
            <strong>Payment / transaction records</strong> — retained as required
            by tax and financial-reporting laws (typically 7 years). These records
            do not include card details — only the store, transaction ID, product
            (subscription or credit pack), date, and credits granted, so refunds can
            be handled correctly.
          </li>
          <li>
            <strong>Reports about other users</strong> — if you previously reported
            another user's content, the report metadata may be retained to protect
            the community even after your account is deleted.
          </li>
        </ul>

        <h2>Retention windows</h2>
        <ul>
          <li>Personal data is purged from live systems within 7 days of the request.</li>
          <li>Encrypted backups are purged within 30 days of the request.</li>
          <li>Anonymized and legally retained records persist beyond this window.</li>
        </ul>

        <h2>Subscription handling</h2>
        <p>
          Deletion does not automatically cancel an active subscription on the
          underlying store. If you subscribed via Apple App Store or Google Play,
          you must cancel the subscription separately:
        </p>
        <ul>
          <li><strong>iOS</strong> — Settings → your Apple ID → Subscriptions</li>
          <li><strong>Android</strong> — Google Play app → Profile → Payments &amp; subscriptions → Subscriptions</li>
        </ul>
        <p>
          If you subscribed via our website, your Stripe subscription is canceled
          automatically when you delete your account.
        </p>

        <h2>Contact</h2>
        <p>
          Any questions? Email{' '}
          <a href="mailto:hello@uhzlab.com">hello@uhzlab.com</a>. We respond
          within two business days.
        </p>
      </main>
    </div>
  );
}

// ─── 한국어 ──────────────────────────────────────────────────────────────
function DeleteAccountKo() {
  return (
    <div className="legal-page">
      <main className="legal-content">
        <h1>archelier 계정 삭제</h1>
        <p className="legal-updated">최종 업데이트: 2026년 10월</p>

        <p>
          언제든 <strong>archelier</strong> 계정과 관련 데이터를 영구 삭제할 수 있어요.
          두 가지 방법이 있습니다.
        </p>

        <h2>방법 1 — 앱 안에서 (권장, 즉시 처리)</h2>
        <ol>
          <li>iOS 또는 Android 의 archelier 앱을 열거나{' '}
            <a href="https://archelier.co" target="_blank" rel="noreferrer">archelier.co</a>{' '}
            에 접속해요.</li>
          <li>가입할 때 사용한 Google 또는 Apple 계정으로 로그인해요.</li>
          <li>하단(모바일) 또는 우상단 아바타 메뉴(데스크탑)의 <strong>설정</strong> 탭을 누름.</li>
          <li>설정 페이지 맨 아래로 내려서 <strong>계정 삭제</strong> 누름.</li>
          <li>확인하면 계정과 모든 데이터가 몇 분 안에 삭제돼요.</li>
        </ol>

        <h2>일부 데이터만 삭제 (계정은 유지)</h2>
        <p>
          계정 전체 삭제 없이 앱 안에서 개별 항목을 지울 수도 있어요.
        </p>
        <ul>
          <li><strong>개별 디자인</strong> — 내 디자인에서 디자인 열고 휴지통 아이콘.</li>
          <li><strong>댓글</strong> — 내 댓글 길게 눌러서 삭제.</li>
          <li><strong>즐겨찾기 / 컬렉션</strong> — 컬렉션 열고 항목 개별 제거.</li>
          <li><strong>프로필 사진 / 표시 이름 / 소개</strong> — 설정 → 프로필 편집.</li>
          <li><strong>팔로우 / 차단</strong> — 프로필에서 언팔로우, 차단은 설정 → 차단 사용자.</li>
          <li><strong>Gio 대화</strong> — 메시지를 하나씩 지우는 기능은 아직 앱에 없어요. 아래 메일로 요청하면 삭제해드려요.</li>
        </ul>
        <p>
          앱 내 컨트롤로 안 지워지는 특정 항목이 필요하면{' '}
          <a href="mailto:hello@uhzlab.com?subject=archelier%20%ED%8A%B9%EC%A0%95%20%EB%8D%B0%EC%9D%B4%ED%84%B0%20%EC%82%AD%EC%A0%9C">
            hello@uhzlab.com
          </a>{' '}
          으로 항목 설명해서 메일 주세요 — 7일 이내 삭제해드려요.
        </p>

        <h2>방법 2 — 이메일로 요청 (앱 없어도 가능)</h2>
        <p>
          앱을 더 이상 설치하고 있지 않다면 가입할 때 쓴 이메일 주소에서{' '}
          <a href="mailto:hello@uhzlab.com?subject=archelier%20%EA%B3%84%EC%A0%95%20%EC%82%AD%EC%A0%9C%20%EC%9A%94%EC%B2%AD">
            hello@uhzlab.com
          </a>{' '}
          으로 제목 <em>"archelier 계정 삭제 요청"</em> 으로 메일 보내주세요.
          7일 이내 확인 + 삭제 완료해드려요.
        </p>

        <h2>삭제되는 데이터</h2>
        <ul>
          <li>계정 프로필 — 표시 이름, 핸들, 소개, 프로필 사진</li>
          <li>생성한 모든 디자인 (게시 안 한 임시 디자인 포함)</li>
          <li>업로드한 모든 사진</li>
          <li>AI 디자인 어시스턴트 Gio 와 나눈 대화와 거기 첨부한 사진</li>
          <li>기기의 푸시 알림 토큰</li>
          <li>모든 댓글, 좋아요, 팔로우, 즐겨찾기, 컬렉션</li>
          <li>제출한 신고와 차단한 사용자</li>
          <li>구독 상태와 크레딧 잔액 (구매한 크레딧 포함 — 남은 크레딧은 환불되지 않아요)</li>
        </ul>

        <h2>보관되는 데이터</h2>
        <ul>
          <li>
            <strong>익명 분석 데이터</strong> — 개인 식별 불가능한 집계 사용 데이터.
            서비스 품질 개선용.
          </li>
          <li>
            <strong>결제 / 거래 기록</strong> — 세무·재무 법령상 보관 의무 (보통 7년).
            카드 번호는 포함 안 함. 환불 처리를 위해 스토어, 거래 ID, 상품(구독 또는 크레딧 팩),
            날짜, 지급한 크레딧만 보관.
          </li>
          <li>
            <strong>다른 사용자에 대한 신고</strong> — 다른 사용자 콘텐츠를 신고했었다면
            커뮤니티 보호 목적상 신고 메타데이터는 계정 삭제 후에도 보관될 수 있음.
          </li>
        </ul>

        <h2>보관 기간</h2>
        <ul>
          <li>개인 데이터는 요청 후 7일 이내 운영 시스템에서 삭제.</li>
          <li>암호화된 백업은 요청 후 30일 이내 완전 삭제.</li>
          <li>익명화 + 법적 보관 의무 데이터는 위 기간 이후에도 유지.</li>
        </ul>

        <h2>구독 관리</h2>
        <p>
          계정 삭제가 활성 구독을 자동으로 해지하지 않습니다. Apple App Store
          또는 Google Play 로 구독한 경우 별도로 해지해야 해요:
        </p>
        <ul>
          <li><strong>iOS</strong> — 설정 앱 → Apple ID → 구독</li>
          <li><strong>Android</strong> — Google Play 앱 → 프로필 → 결제 및 구독 → 구독</li>
        </ul>
        <p>
          archelier.co 웹사이트로 구독한 경우엔 계정 삭제 시 Stripe 구독이 자동 해지됩니다.
        </p>

        <h2>문의</h2>
        <p>
          질문 있으면{' '}
          <a href="mailto:hello@uhzlab.com">hello@uhzlab.com</a> 으로 연락주세요.
          2영업일 안에 답장 드려요.
        </p>
      </main>
    </div>
  );
}

// ─── 日本語 ──────────────────────────────────────────────────────────────
function DeleteAccountJa() {
  return (
    <div className="legal-page">
      <main className="legal-content">
        <h1>archelier アカウントを削除</h1>
        <p className="legal-updated">最終更新: 2026年10月</p>

        <p>
          いつでも <strong>archelier</strong> アカウントと関連データを完全に削除できます。
          2 つの方法があります。
        </p>

        <h2>方法 1 — アプリ内から（推奨・即時反映）</h2>
        <ol>
          <li>iOS または Android で archelier アプリを開くか、{' '}
            <a href="https://archelier.co" target="_blank" rel="noreferrer">archelier.co</a>{' '}
            にアクセスします。</li>
          <li>登録時に使用した Google または Apple アカウントでログイン。</li>
          <li>下部（モバイル）または右上のアバターメニュー（デスクトップ）から
            <strong>設定</strong> タブを開きます。</li>
          <li>設定ページの一番下までスクロール →{' '}
            <strong>アカウント削除</strong> をタップ。</li>
          <li>確認すると、アカウントとすべてのデータが数分以内に削除されます。</li>
        </ol>

        <h2>一部データのみ削除（アカウントは維持）</h2>
        <p>
          アカウント全体を削除せず、アプリ内で個別に項目を削除することもできます。
        </p>
        <ul>
          <li><strong>個別のデザイン</strong> — マイデザインで対象を開き、ゴミ箱アイコンをタップ。</li>
          <li><strong>コメント</strong> — 自分のコメントを長押しして削除。</li>
          <li><strong>ブックマーク / コレクション</strong> — コレクションを開き、項目を個別に削除。</li>
          <li><strong>プロフィール写真 / 表示名 / 自己紹介</strong> — 設定 → プロフィール編集。</li>
          <li><strong>フォロー / ブロック</strong> — プロフィールからアンフォロー、ブロックは設定 → ブロック済みユーザー。</li>
          <li><strong>Gio との会話</strong> — メッセージを個別に削除する機能はまだアプリにありません。下記メールでご依頼いただければ削除します。</li>
        </ul>
        <p>
          アプリ内の操作で消せない特定の項目があれば、{' '}
          <a href="mailto:hello@uhzlab.com?subject=archelier%20%E7%89%B9%E5%AE%9A%E3%83%87%E3%83%BC%E3%82%BF%E5%89%8A%E9%99%A4">
            hello@uhzlab.com
          </a>{' '}
          まで対象を説明してメールください — 7 日以内に削除します。
        </p>

        <h2>方法 2 — メールで依頼（アプリ不要）</h2>
        <p>
          アプリをすでにアンインストール済みの場合、登録時のメールアドレスから{' '}
          <a href="mailto:hello@uhzlab.com?subject=archelier%20%E3%82%A2%E3%82%AB%E3%82%A6%E3%83%B3%E3%83%88%E5%89%8A%E9%99%A4%E4%BE%9D%E9%A0%BC">
            hello@uhzlab.com
          </a>{' '}
          へ件名 <em>「archelier アカウント削除依頼」</em> でメールください。
          7 日以内に確認 + 削除完了の返信をお送りします。
        </p>

        <h2>削除されるデータ</h2>
        <ul>
          <li>アカウントプロフィール — 表示名、ハンドル、自己紹介、プロフィール写真</li>
          <li>作成したすべてのデザイン（未投稿の下書き含む）</li>
          <li>アップロードしたすべての写真</li>
          <li>AI デザインアシスタント Gio との会話と、そこに添付した写真</li>
          <li>端末のプッシュ通知トークン</li>
          <li>すべてのコメント、いいね、フォロー、ブックマーク、コレクション</li>
          <li>送信した通報と設定したブロック</li>
          <li>サブスクリプション状態とクレジット残高（購入したクレジットを含む — 残りのクレジットは返金されません）</li>
        </ul>

        <h2>保持されるデータ</h2>
        <ul>
          <li>
            <strong>匿名化された分析データ</strong> — 個人を特定できない集計利用データ。
            サービス品質改善のため。
          </li>
          <li>
            <strong>決済・取引記録</strong> — 税務・財務報告法令に基づき保管が義務付けられている
            データ（通常 7 年間）。カード情報は含まず、返金処理のためにストア、取引 ID、商品（サブスクリプションまたはクレジットパック）、日付、付与したクレジットのみを保管。
          </li>
          <li>
            <strong>他のユーザーに関する通報</strong> — 過去に他ユーザーのコンテンツを通報した場合、
            コミュニティ保護のため通報メタデータはアカウント削除後も保持される場合があります。
          </li>
        </ul>

        <h2>保持期間</h2>
        <ul>
          <li>個人データは依頼から 7 日以内に本番システムから削除。</li>
          <li>暗号化バックアップは依頼から 30 日以内に完全消去。</li>
          <li>匿名化データおよび法的保持義務データはこの期間以降も維持。</li>
        </ul>

        <h2>サブスクリプションの取り扱い</h2>
        <p>
          アカウント削除はアクティブなサブスクリプションを自動キャンセルしません。
          Apple App Store または Google Play で契約した場合は、別途キャンセル操作が必要です：
        </p>
        <ul>
          <li><strong>iOS</strong> — 設定 → Apple ID → サブスクリプション</li>
          <li><strong>Android</strong> — Google Play アプリ → プロフィール → お支払いと定期購入 → 定期購入</li>
        </ul>
        <p>
          archelier.co の Web サイトで契約した場合は、アカウント削除と同時に
          Stripe サブスクリプションが自動キャンセルされます。
        </p>

        <h2>お問い合わせ</h2>
        <p>
          ご不明な点は{' '}
          <a href="mailto:hello@uhzlab.com">hello@uhzlab.com</a> までご連絡ください。
          営業日 2 日以内にご返信します。
        </p>
      </main>
    </div>
  );
}
